import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';

export interface IPaymentSearchParams {
  page?: number;
  pageSize?: number;
  sortBy?: 'createdAt' | 'amount' | 'status' | 'updatedAt';
  sortOrder?: 'ASC' | 'DESC';
  status?: string[];
  refundStatus?: string[];
  processor?: string[];
  from?: string;
  to?: string;
  storeId?: string[];
  organizationId?: string[];
  userId?: string;
  orderId?: string;
  minAmount?: number;
  maxAmount?: number;
  cur?: string;
  currencyOriginalCode?: string;
  currencyConvertedCode?: string;
  taxRateId?: string;
  search?: string;
}

function buildParams(params: IPaymentSearchParams): URLSearchParams {
  const qs = new URLSearchParams();
  qs.append('pagination', 'true');
  qs.append('page', String(params.page ?? 1));
  qs.append('pageSize', String(params.pageSize ?? 20));
  qs.append('sortBy', params.sortBy ?? 'createdAt');
  qs.append('sortOrder', params.sortOrder ?? 'DESC');
  for (const v of params.status ?? []) qs.append('status', v);
  for (const v of params.refundStatus ?? []) qs.append('refundStatus', v);
  for (const v of params.processor ?? []) qs.append('processor', v);
  if (params.from) qs.append('from', params.from);
  if (params.to) qs.append('to', params.to);
  for (const v of params.storeId ?? []) qs.append('storeId', v);
  for (const v of params.organizationId ?? []) qs.append('organizationId', v);
  if (params.userId) qs.append('userId', params.userId);
  if (params.orderId) qs.append('orderId', params.orderId);
  if (params.minAmount !== undefined)
    qs.append('minAmount', String(params.minAmount));
  if (params.maxAmount !== undefined)
    qs.append('maxAmount', String(params.maxAmount));
  if (params.cur) qs.append('cur', params.cur);
  if (params.currencyOriginalCode)
    qs.append('currencyOriginalCode', params.currencyOriginalCode);
  if (params.currencyConvertedCode)
    qs.append('currencyConvertedCode', params.currencyConvertedCode);
  if (params.taxRateId) qs.append('taxRateId', params.taxRateId);
  if (params.search?.trim()) qs.append('search', params.search.trim());
  return qs;
}

export const PaymentService = {
  /**
   * Recherche les paiements visibles par l'utilisateur courant, avec tous les filtres du contrat.
   */
  searchPayments: async (params: IPaymentSearchParams) => {
    try {
      const qs = buildParams(params);
      const response = await axiosInstance.get<any>(
        `payments?${qs.toString()}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère le détail d'un paiement (avec transactions provider, commande, client, commerce).
   */
  getPaymentById: async (paymentId: string) => {
    try {
      const response = await axiosInstance.get<any>(`payments/${paymentId}`);
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Statistiques agrégées de paiements sur une fenêtre temporelle.
   */
  getPaymentStats: async (params: {
    from?: string;
    to?: string;
    granularity?: 'day' | 'week' | 'month';
    timezone?: string;
    storeId?: string[];
    organizationId?: string[];
    processor?: string[];
  }) => {
    try {
      const qs = new URLSearchParams();
      if (params.from) qs.append('from', params.from);
      if (params.to) qs.append('to', params.to);
      if (params.granularity) qs.append('granularity', params.granularity);
      if (params.timezone) qs.append('timezone', params.timezone);
      for (const v of params.storeId ?? []) qs.append('storeId', v);
      for (const v of params.organizationId ?? [])
        qs.append('organizationId', v);
      for (const v of params.processor ?? []) qs.append('processor', v);
      const response = await axiosInstance.get<any>(
        `payments/stats?${qs.toString()}`,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Rembourse un paiement, totalement (sans `amount`) ou partiellement (avec `amount`).
   */
  refundPayment: async (paymentId: string, reason: string, amount?: number) => {
    try {
      const body: { reason: string; amount?: number } = { reason };
      if (amount !== undefined) body.amount = amount;
      const response = await axiosInstance.post<any>(
        `payments/${paymentId}/refund`,
        body,
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Resynchronise un paiement avec son provider.
   */
  reconcilePayment: async (paymentId: string) => {
    try {
      const response = await axiosInstance.post<any>(
        `payments/${paymentId}/reconcile`,
        {},
      );
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
